/**
 * @file     MapBrowseCta.tsx
 * @purpose  The 68px map browse row from the hub kit: a theme-safe path from
 *           an SEO landing page or a listing grid into the interactive map,
 *           carrying the filter the page is already about. Shared by hub views
 *           and LocationListingGrid.
 */
'use client';

import Link from 'next/link';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import SvgIcon from '@mui/material/SvgIcon';
import { useAppTheme } from '@/core/theme/useAppTheme';
import { semanticRoleColors } from '@/design-system/tokens';

function ArrowIcon() {
  return (
    <SvgIcon fontSize="small" aria-hidden>
      <path d="m13.2 5.2 5.8 5.8-5.8 5.8-1.4-1.4 3.4-3.4H5v-2h10.2l-3.4-3.4 1.4-1.4Z" />
    </SvgIcon>
  );
}

function MapIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M9 4 3 6v14l6-2 6 2 6-2V4l-6 2-6-2zM9 4v14M15 6v14" />
    </svg>
  );
}

export interface MapBrowseCtaProps {
  href: string;
  label: string;
  sublabel?: string;
  /**
   * Drop the row's own outer margins when a parent (a grid header stack)
   * already owns the spacing. Standalone placements keep the default.
   */
  flush?: boolean;
}

export function MapBrowseCta({ href, label, sublabel, flush = false }: MapBrowseCtaProps) {
  const { theme } = useAppTheme();
  const c = theme.semanticColors;
  return (
    <Box
      component={Link}
      href={href}
      data-testid="map-browse-cta"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        px: { xs: 4, sm: 5 },
        py: 3,
        ...(flush ? { m: 0 } : { mt: 0, mb: 3 }),
        minHeight: 68,
        borderRadius: `${theme.layout.radius.lg}px`,
        border: `1px solid ${c.borderSubtle}`,
        backgroundColor: c.surfaceCard,
        textDecoration: 'none',
        color: c.textPrimary,
        transition: `border-color ${theme.motion.durations.normal}ms ${theme.motion.easing.standard}, background-color ${theme.motion.durations.normal}ms ${theme.motion.easing.standard}`,
        '&:hover': { borderColor: c.interactivePrimary, backgroundColor: c.surfaceElevated },
        '&:focus-visible': { outline: `2px solid ${c.interactiveFocusRing}`, outlineOffset: 2 },
      }}
    >
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: { xs: 2, sm: 3 }, minWidth: 0 }}>
        <Box
          aria-hidden
          sx={{
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 40,
            height: 40,
            borderRadius: `${theme.layout.radius.sm}px`,
            backgroundColor: semanticRoleColors.chip,
            color: semanticRoleColors.chipText,
          }}
        >
          <MapIcon />
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 0 }}>
          <Typography sx={{ fontSize: 15.5, fontWeight: 600, letterSpacing: '-0.01em', lineHeight: 1.3 }}>
            {label}
          </Typography>
          {sublabel ? (
            <Typography sx={{ fontSize: 13, lineHeight: 1.4, color: c.textSecondary }}>{sublabel}</Typography>
          ) : null}
        </Box>
      </Box>
      <Box aria-hidden sx={{ flexShrink: 0, display: 'flex', color: semanticRoleColors.link }}>
        <ArrowIcon />
      </Box>
    </Box>
  );
}
